import React, { useCallback, useRef, useMemo, useEffect, useState, useLayoutEffect } from 'react';
import styles from './FullScreenWrapper.module.scss';
import FSIcon from '@src/assets/icons/fullscreen.svg?react';
import FSBackIcon from '@src/assets/icons/exit_lesson.svg?react';
import FSExitIcon from '@src/assets/icons/fullscreen_exit.svg?react';
import IconPDF from '@src/assets/icons/content_progress_pdf.svg?react';
import IconAudio from '@src/assets/icons/content_progress_audio.svg?react';
import IconSlide from '@src/assets/icons/content_progress_slide.svg?react';
import IconVideo from '@src/assets/icons/content_progress_video.svg?react';
import IconForm from '@src/assets/icons/content_progress_feedback.svg?react';
import { ContentType, SllLesson, SllModule } from '../../../types';
import { useFullScreen } from '@hooks/useFullScreen';
import { useMediaQuery } from 'react-responsive';
import CompleteModal from '../../program-details/complete-modal/CompleteModal';
import ExitModal from '../../program-details/exit-modal/ExitModal';
import { useNextContent } from '../useNextContent';
import { isHomeEDApp } from '@src/constants';
import { useAnalytics, Events, useMarkLessonComplete, useUser } from '@hooks';
import { useStandaloneContent } from '@src/hooks/useStandaloneContent';
import TranslationBadge from "@src/components/translation-provider/TranslationBadge";
import { usePresentationContext } from '@src/hooks/usePresentationContext';

type Props = {
  content?: SllModule;
  lesson?: SllLesson;
  needFullscreen?: boolean;
  closeFullScreen?: () => void;
  goBack?: () => void;
  children: (isFullScreen: boolean) => React.ReactNode;
};

const getContentIcon = (type: ContentType) => {
  switch (type) {
    case ContentType.PDF: {
      return IconPDF;
    }
    case ContentType.AUDIO: {
      return IconAudio;
    }
    // case ContentType.GOOGLESLIDE:
    case ContentType.SLIDE: {
      return IconSlide;
    }
    case ContentType.VIDEO: {
      return IconVideo;
    }
    case ContentType.FORM: {
      return IconForm;
    }
    default: {
      return null;
    }
  }
}

function NewLayoutFullScreenWrapper(props: Props) {
  const { children, content, lesson, goBack, needFullscreen, closeFullScreen } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const topbar = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const { open, close, fullScreen } = useFullScreen(wrapperRef);
  const [isHovered, setIsHovered] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  // const [isComplete, setIsComplete] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: 915 });
  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [/(Android)(.+)(Mobile)/i, /BlackBerry/i, /iPhone|iPod/i, /Opera Mini/i, /IEMobile/i]
    return regexs.some((b) => userAgent.match(b))
  }, [])

  const {
    section,
    startedAlternateLesson,
    setLastContent,
    showExitTicket,
    pageNumber,
    isPreProgramQuiz,
    quizModules,
    programName,
  } = useStandaloneContent();
  const {isPreview} = usePresentationContext();

  const { hasNextContent, hasPrevContent } = useNextContent();
  const { trackEvent } = useAnalytics();
  const [user] = useUser();
  
  // Check if user is from Australia
  const isAUuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("au");
  }, [user?.id]);

  const onMarkLessonCompleted = useCallback(() => {
    setCompleteModal(false);
    // setIsComplete(true);
  }, []);

  const { sllMarkLessonComplete, sllLessonNotStarted, loading: isMarkLessonCompleteLoading } = useMarkLessonComplete(onMarkLessonCompleted, goBack, undefined);

  const enterFullScreen = useCallback(() => {
    open();
  }, [open]);

  const exitFullScreen = useCallback(() => {
    close();
    closeFullScreen?.();
  }, [close, closeFullScreen]);

  useEffect(() => {
    if (needFullscreen) {
      open();
    }
  }, [needFullscreen, open])


  const handleEndLesson = useCallback(() => {
    if (!hasNextContent && content?.lastInLesson) {
      if (/*isComplete &&*/ goBack) {
        goBack()
      } else {
        setCompleteModal(true);
      }
      trackEvent(startedAlternateLesson ? Events.actions.exitSection : Events.actions.exitLesson, {
        standalone_lesson: true,
        lesson_name: lesson?.name,
        section_name: section?.name,
        section_number: section?.position,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    } else if (goBack) {
      if ((hasPrevContent || pageNumber > 1) && !isHomeEDApp && !startedAlternateLesson) {
        setShowExitModal(true);
      } else {
        goBack();
      }
    }
  }, [hasNextContent, content?.lastInLesson, goBack, trackEvent, startedAlternateLesson, lesson?.name, section?.name, section?.position, hasPrevContent, pageNumber, user])

  const handleNotStartedLesson = useCallback(async () => {
    if (lesson) {
      await sllLessonNotStarted(
        lesson?.id,
      )
    }
  }, [lesson, sllLessonNotStarted]);

  const onSave = useCallback(async () => {

    setShowExitModal(false);
    if (goBack) {
      goBack();
    }

    trackEvent(startedAlternateLesson ? Events.actions.exitSection : Events.actions.exitLesson, {
      standalone_lesson: true,
      lesson_name: lesson?.name,
      section_name: section?.name,
      section_number: section?.position,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [goBack, lesson?.name, section?.name, section?.position, startedAlternateLesson, user, trackEvent]);

  const onNoSave = useCallback(async () => {
    setShowExitModal(false);
    if (goBack) {
      goBack();
    }

    trackEvent(startedAlternateLesson ? Events.actions.exitSection : Events.actions.exitLesson, {
      standalone_lesson: true,
      lesson_name: lesson?.name,
      section_name: section?.name,
      section_number: section?.position,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [goBack, lesson?.name, section?.name, section?.position, startedAlternateLesson, user, trackEvent]);

  const handleCompleteLesson = useCallback(async () => {
    if (lesson) await sllMarkLessonComplete(lesson, programName);
  }, [lesson, programName, sllMarkLessonComplete]);

  useEffect(() => {
    let timeout: any;

    const displayOpa = () => {
      if (topbar.current) {
        topbar.current.style.opacity = "1"
      }
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        if (topbar.current && document.fullscreenElement) {
          topbar.current.style.opacity = "0"
        }

      }, 5000);
    };
    let refWrapper: any = null;
    if (wrapperRef.current) {
      refWrapper = wrapperRef.current
      refWrapper.addEventListener('mousemove', displayOpa);
    }

    return () => {
      if (refWrapper) {
        refWrapper.removeEventListener('mousemove', displayOpa);
      }
    };

  }, [wrapperRef]);

  const modules = useMemo(() => {
    // Alternate Lesson
    if (section && section.modules?.find(item => item?.id === content?.id)) {
      return section.modules
    }
    // Main Lesson
    if (lesson && lesson.presentation?.find(item => item?.id === content?.id)) {
      const preExitTicket = lesson.presentation.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.SLIDE)
      const exitTicket = lesson.presentation.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.FORM)
      if(preExitTicket && exitTicket) {
        return lesson.presentation.filter(module => module?.lastInLesson !== true && module !== preExitTicket); // Remove "Lesson Complete" and "PreExitTicket" module on Progress
      }
      return lesson.presentation.filter(module => module?.lastInLesson !== true); // Remove Lesson Complete module on Progress
    // Pre-Program Quiz Modules
    } else if (isPreProgramQuiz && quizModules) {
      const normalModules = quizModules.filter(content => content.type !== ContentType.FORM)
      const quizModule = quizModules.filter(content => content.type === ContentType.FORM)
      const modules = [...normalModules, quizModule[0]]
      return modules
    } else {
      return []
    }
  }, [content, lesson, section, isPreProgramQuiz, quizModules])
  // check which one is class presentation
  const moduleIndex = useMemo(() => {
    // Alternate Lesson
    if (section && section.modules?.find(item => item?.id === content?.id)) {
      return section.modules.findIndex(m => m?.id === content?.id);
    }
    // Pre-Program Quiz Modules
    if (isPreProgramQuiz && quizModules) {
      return quizModules.findIndex(m => m?.id === content?.id);
    }
    // Main Lesson
    if (lesson && lesson.presentation?.find(item => item?.id === content?.id)) {
      const progressModules = lesson.presentation.filter(module => module?.lastInLesson !== true);
      const lessonCompleteModule = lesson.presentation.find(module => module?.lastInLesson === true);
      if (lessonCompleteModule && lessonCompleteModule.id === content?.id) {
        return lesson.presentation.findIndex(module => module === lessonCompleteModule);
      }
      return progressModules.findIndex(m => m?.id === content?.id);
    }
    return -1
  }, [content, lesson, section, isPreProgramQuiz, quizModules])

  const survey = useMemo(() => {  // Exit Ticket
    if (content?.type === ContentType.FORM && showExitTicket) {
      return true
    } else {
      return false
    }
  }, [content, showExitTicket])

  const calculateWidth = useCallback(() => {
    // Alternate Lesson
    if (startedAlternateLesson && section && section.modules?.find(item => item?.id === content?.id) && progressRef.current) {
      var contentIndex = modules.findIndex(m => m?.id === content?.id);
      if(contentIndex > 0) {
        if(contentIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth - 50) * contentIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    } // Main Lesson
    else if (lesson && lesson.presentation?.find(item => item?.id === content?.id) && progressRef.current) {
      var lessonmodules = lesson.presentation.filter(module => module?.lastInLesson !== true);
      const preExitTicket = lessonmodules.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.SLIDE)
      const exitTicket = lessonmodules.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.FORM)
      const lessonCompleteModule = lesson.presentation.find(module => module?.lastInLesson === true);

      var moduleIndex = modules.findIndex(m => m?.id === content?.id);
      if (preExitTicket && exitTicket && preExitTicket.id === content?.id) {
        moduleIndex = modules.findIndex(m => m?.id === exitTicket.id)
      } else if (lessonCompleteModule && lessonCompleteModule.id === content?.id) {
        moduleIndex = lesson.presentation.findIndex(m => m?.id === lessonCompleteModule.id)
      }
      if(moduleIndex > 0) {
        if(moduleIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth - 50) * moduleIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    }
     // Quiz Progress
     else if (isPreProgramQuiz && quizModules && quizModules.find(item => item?.id === content?.id) && progressRef.current) {
      var quizIndex = modules.findIndex(m => m?.id === content?.id);
      if(quizIndex > 0) {
        if(quizIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth - 50) * quizIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    }
  },[content, startedAlternateLesson, lesson, section, modules, setLastContent, isPreProgramQuiz, quizModules])

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateWidth);

    calculateWidth();
    return () => {
      window.removeEventListener('resize', calculateWidth);
    };
  }, [calculateWidth]);

  const fullScreenButtonBlock = useMemo(() => {
    const percent = modules.length === 1 ? 100 : (moduleIndex + 1) / modules.length * 100
    const isClassPresentation = moduleIndex > -1;

    return (
      <div ref={topbar} className={`${(fullScreen) ? styles.fullScreenActions : styles.actions} ${(fullScreen) ? styles.noPadding : ''}`} style={{backgroundColor: fullScreen ? 'black': 'white', display: fullScreen ? 'flex' : 'none'}}>
        {(fullScreen)
          ? (
          <button onClick={handleEndLesson} className={`${styles.button} ${(fullScreen || isClassPresentation) ? styles.buttonFull : ''}`}
           type={'button'} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <div className={styles.backIconWrapper}>
              <FSBackIcon/>
            </div>
          </button>
          ) : null
        }
        <div className={styles.backTipView} style={{display: isHovered ? 'flex': 'none'}}>
          <div className={styles.arrowUp}></div>
          <div className={styles.tipTextView}>
            <div className={styles.tipTitle}>Exit Lesson</div>
          </div>
        </div>
        {((fullScreen && isClassPresentation)) && <div ref={progressRef} className={styles.progressBackBar}>
          <div className={styles.progressBar} style={{width: `${progressWidth}px`, borderRadius: percent === 100 ? '3px' : '' }}></div>
          <div className={styles.iconView}>{modules.map((item: any) => {
            if (item.type === ContentType.PARENT) {
              return <div key={item.id}>PS</div>
            } else {
              const Icon = getContentIcon(item.type);
              return (
                <div key={item.id}>
                  {Icon && <Icon className={styles.moduleIcon}/>}
                </div>
              )
            }})}
          </div>
        </div>
        }
        {/* Translation Badge for full screen mode - hidden for Australian users */}
        {!isAUuser && (
          <TranslationBadge type={isPreview ? "preview" : "presentation"} />
        )}
        
        {(fullScreen ? <button onClick={exitFullScreen} className={`${styles.button} ${fullScreen ? styles.buttonFull : ''}`} type={'button'}>
          <div className={styles.iconWrapper}>
            <FSExitIcon/>
          </div>
          {(!isMobile) && <span className={styles.textWhite}>Exit full screen</span>}
        </button> : <button onClick={enterFullScreen} className={`${styles.button} ${fullScreen ? styles.buttonFull : ''}`} type={'button'}>
          <div className={styles.iconWrapper}>
            <FSIcon/>
          </div>
          {(!isMobile) && <span className={styles.textWhite}>Open full screen</span>}
        </button>)}
      </div>
    );
  }, [modules, moduleIndex, fullScreen, handleEndLesson, isHovered, progressWidth, isAUuser, isPreview, exitFullScreen, isMobile, enterFullScreen]);

  return (
    <div id="fswrapper" className={styles.wrapper} ref={wrapperRef} style={{backgroundColor: (survey || !fullScreen) ? 'white': isPhone ? '#535353' : 'black'}}>
      {!isPhone ? fullScreenButtonBlock : null}
      <div className={`${styles.content} ${fullScreen ? `${isMobile ? styles.isMobileFull : ''}` : ''} ${isPhone ? styles.isMobileFull : ''}`} style={{marginTop: 0}}>
        {children(fullScreen)}
      </div>
      {completeModal && <CompleteModal open={completeModal} loading={isMarkLessonCompleteLoading} onClose={() => goBack && goBack()} onContinue={() => setCompleteModal(false)} onComplete={handleCompleteLesson} onJustView={handleNotStartedLesson} completed={survey} />}
      {showExitModal && <ExitModal open={showExitModal} onSave={onSave} onNoSave={onNoSave} onClose={() => setShowExitModal(false)} loading={false} />}
    </div>
  );
}

export default NewLayoutFullScreenWrapper;
